import React, { useContext, useState, useEffect } from 'react';
import { MitWebsocket } from 'src/common/managers/MitWebsocket';
import { BroadcastReceiverContext, BroadcastMessageInfoFormatWithMetaData } from 'src/states/BroadcastReceiverState';
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { GlassButton } from './GlassButtons';
import { Localization, SessionContext } from '@viamap/viamap2-common';
import { ConfirmationDialog } from './ConfirmationDialog';
import { MIBIcon } from 'src/componentsUtils/MIBIcon';
import { DK, EN, TranslationInline } from './MitGlassComponents';

export function BroadcastMessage(props: { window: ManagedWindow, onClose: () => void }) {
   const { state: _broadcastReceiverState } = useContext(BroadcastReceiverContext);
   const { state: _sessionState } = useContext(SessionContext);
   const activeTab = 'text' as const;
   const [textBroadcast, setTextBroadcast] = useState({
      title: '',
      message: '',
      type: 'All' as 'All' | 'Org' | 'User',
      SendTo: ''
   });
   const [showConfirmDialog, setShowConfirmDialog] = useState(false);
   const [showSuccessMessage, setShowSuccessMessage] = useState(false);
   const [errorMessage, setErrorMessage] = useState('');

   const sendTextBroadcast = () => {
      // Clear any previous error message
      setErrorMessage('');

      if (!textBroadcast.title.trim() || !textBroadcast.message.trim()) {
         setErrorMessage(Localization.getText("Please fill in both title and message"));
         return;
      }

      if ((textBroadcast.type === 'Org' || textBroadcast.type === 'User') && !textBroadcast.SendTo.trim()) {
         setErrorMessage(Localization.getText("Please specify the organization or user name"));
         return;
      }

      if (textBroadcast.type === 'All') {
         setShowConfirmDialog(true);
         return;
      }

      actuallySendMessage();
   };

   const actuallySendMessage = () => {
      const message: BroadcastMessageInfoFormatWithMetaData = {
         header: textBroadcast.title,
         message: textBroadcast.message,
         type: textBroadcast.type,
         sendTo: textBroadcast.SendTo
      };

      MitWebsocket.send({
         type: "sendToChannel",
         action: { channel: "broadcastCustom", data: message }
      });

      // Show success message
      setShowSuccessMessage(true);
      setShowConfirmDialog(false);
   };

   // debugState removed (unused)

   // Auto-hide success message after 3 seconds
   useEffect(() => {
      if (showSuccessMessage) {
         const timer = setTimeout(() => {
            setShowSuccessMessage(false);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [showSuccessMessage]);

   // Clear error message when user starts typing
   const clearError = () => {
      if (errorMessage) {
         setErrorMessage('');
      }
   };

   // Check if text broadcast form is valid
   const isTextBroadcastValid = () => {
      if (!textBroadcast.title.trim() || !textBroadcast.message.trim()) {
         return false;
      }
      if ((textBroadcast.type === 'Org' || textBroadcast.type === 'User') && !textBroadcast.SendTo.trim()) {
         return false;
      }
      return true;
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("Broadcast Message")}
         window={props.window}
         onClose={props.onClose}
         PosDefault={{ left: "50%", top: "20%", width: "50%", height: "70%", transform: "translate(-50%, 0)" }}
      >
         <ADModalBody>
            <div className="broadcast-debug-content">
               {activeTab === 'text' && (
                  <>
                     <h4 className="broadcast-debug-section-title">{Localization.getText("Custom Text Broadcast")}</h4>
                     <p className="broadcast-debug-description">
                        <TranslationInline>
                           <DK>
                              Send en besked til onlinebrugere og brugere, der logger på inden for de næste 6 timer.<br />
                              Beskeden vil <b>overskrive</b> alle tidligere beskeder.
                           </DK>
                           <EN>
                              Send a message to online users and users who will come within the next 6 hours. <br />
                              The message will <b>override</b> all previous messages.
                           </EN>
                        </TranslationInline>
                     </p>

                     {errorMessage && (
                        <div style={{
                           color: '#f44336',
                           backgroundColor: '#ffebee',
                           border: '1px solid #f44336',
                           borderRadius: '4px',
                           padding: '10px',
                           marginBottom: '15px',
                           fontSize: '14px',
                           fontWeight: '500'
                        }}>
                           {errorMessage}
                        </div>
                     )}

                     <div className="broadcast-debug-form">
                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Message Receiver")}
                           </label>
                           <select
                              value={textBroadcast.type}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, type: e.target.value as any, SendTo: '' }));
                              }}
                              className="broadcast-debug-form-select"
                           >
                              <option value="All">{Localization.getText("All")}</option>
                              <option value="Org">{Localization.getText("Organization")}</option>
                              <option value="User">{Localization.getText("User")}</option>
                           </select>
                        </div>

                        {/* Conditional SendTo field - only show for Org or User */}
                        {(textBroadcast.type === 'Org' || textBroadcast.type === 'User') && (
                           <div className="broadcast-debug-form-group">
                              <label className="broadcast-debug-form-label">
                                 {textBroadcast.type === 'Org' ? Localization.getText("Organization (domain, i.e. viamap.net)") : Localization.getText("User (email, i.e. user@viamap.net)")} *
                              </label>
                              <input
                                 type="text"
                                 value={textBroadcast.SendTo}
                                 onChange={(e) => {
                                    clearError();
                                    setTextBroadcast(prev => ({ ...prev, SendTo: e.target.value }));
                                 }}
                                 placeholder={textBroadcast.type === 'Org' ? Localization.getText("Enter organization name...") : Localization.getText("Enter user name...")}
                                 className="broadcast-debug-form-input"
                              />
                           </div>
                        )}

                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Title")} *
                           </label>
                           <input
                              type="text"
                              value={textBroadcast.title}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, title: e.target.value }));
                              }}
                              placeholder={Localization.getText("Enter broadcast title...")}
                              className="broadcast-debug-form-input"
                           />
                        </div>

                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Message")} *
                           </label>
                           <textarea
                              value={textBroadcast.message}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, message: e.target.value }));
                              }}
                              placeholder={Localization.getText("Enter broadcast message...")}
                              rows={4}
                              className="broadcast-debug-form-textarea"
                           />
                        </div>
                     </div>
                  </>
               )}
            </div>
         </ADModalBody>
         <div style={{ height: '10px' }}></div>
         <ADModalFooter>
            {showSuccessMessage && (
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  color: '#4CAF50',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: showSuccessMessage ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
               }}>
                  <MIBIcon icon="allOk" />
                  <span>{Localization.getText("Message sent successfully!")}</span>
               </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
               <GlassButton title={"Send"} onClick={sendTextBroadcast} className="broadcast-debug-send-btn" disabled={!isTextBroadcastValid()}>
                  {Localization.getText("Send Text Broadcast")}
               </GlassButton>
            </div>
         </ADModalFooter>

         <ConfirmationDialog
            showWindow={showConfirmDialog ? 1 : 0}
            header="Confirm Broadcast to All Users"
            onSubmit={() => {
               actuallySendMessage();
               setShowConfirmDialog(false);
            }}
            onCancel={() => setShowConfirmDialog(false)}
         >
            <div>
               <div>
                  {Localization.getText("You are about to send a message to ALL users. This is a significant action that will be seen by everyone.")}
               </div>
               <div>
                  <p></p>
               </div>
               <div>
                  {Localization.getText("Title")}: {textBroadcast.title}
               </div>
               <div>
                  {Localization.getText("Message")}: {textBroadcast.message}
               </div>
               <div>
                  <p></p>
               </div>
               <div>
                  {Localization.getText("Are you sure you want to continue?")}
               </div>
            </div>
         </ConfirmationDialog>
      </AdvancedDragModal>
   );
}